import React, { useMemo, useState } from 'react';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { 
  Typography, 
  Button, 
  Table, 
  Space, 
  Modal, 
  Form, 
  Input, 
  // ...existing code...
  message,
  Popconfirm,
  Select,
  Tag,
  Spin,
  Alert,
  InputNumber,
  Checkbox,
  Upload,
  Row,
  Col,
  Divider,
  Image,
  Tooltip,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  AppstoreOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { Product } from '../../../shared/types';
import { 
  useProducts,
  useCreateProduct,
  useUpdateProduct,
  useDeleteProduct,
} from '../hooks/useProducts';
import { useN8nInstances } from '../hooks/useN8nInstances';
import { useN8nWorkflows } from '../hooks/useN8nWorkflows';
import { useForms } from '../hooks/useForms';
import { functions, supabase } from '../../../shared/lib/supabase';
import type { RcFile, UploadFile } from 'antd/es/upload/interface';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

export const ProductsPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [configuringProduct, setConfiguringProduct] = useState<Product | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | undefined>(undefined);
  const [selectedProductType, setSelectedProductType] = useState<'marketplace' | 'custom'>('marketplace');
  // ...existing code...
  const [productTypeFilter, setProductTypeFilter] = useState<'all' | 'marketplace' | 'custom'>('all');
  const [productConfig, setProductConfig] = useState<any>(null);
  const queryClient = useQueryClient();
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  const [form] = Form.useForm();
  const [configForm] = Form.useForm();
  const [thumbnailFileList, setThumbnailFileList] = useState<UploadFile[]>([]);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  

  // Buscar clientes para selector de produto custom
  const { data: clients = [], isLoading: loadingClients } = useQuery({
    queryKey: ['admin-users-list'],
    queryFn: async () => {
      const response = await functions.listClients();
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch clients');
      }
      return response.clients;
    },
  });

  // TanStack Query hooks
  const { data: products = [], isLoading } = useProducts();
  const { data: instances = [] } = useN8nInstances(); // Mostrar TODAS as instâncias
  
  // Filtrar produtos por tipo
  const filteredProducts = useMemo(() => {
    if (productTypeFilter === 'all') return products;
    return products.filter(p => p.product_type === productTypeFilter);
  }, [products, productTypeFilter]);
  
  // Buscar workflows da instância selecionada (filtrados por tag 'Template' ou 'personalizado')
  const { data: allWorkflows = [], isLoading: isLoadingWorkflows } = useN8nWorkflows(
    selectedInstanceId,
    selectedProductType === 'custom' ? 'personalizado' : 'Template'
  );
  
  // Filtrar workflows que já estão sendo usados em produtos (exceto se estiver editando)
  const workflows = useMemo(() => {
    if (!allWorkflows || allWorkflows.length === 0) return [];
    if (editingProduct) return allWorkflows; // Ao editar, mostrar o workflow atual
    
    // Buscar todos os workflow_ids já usados
    const usedWorkflowIds = new Set(
      products
        .filter(p => p.n8n_instance_id === selectedInstanceId)
        .map(p => p.workflow_id)
    );
    
    // Retornar apenas workflows não usados
    return allWorkflows.filter((w: any) => !usedWorkflowIds.has(w.id));
  }, [allWorkflows, products, selectedInstanceId, editingProduct]);
  
  // Buscar formulários cadastrados
  const { data: forms = [], isLoading: isLoadingForms } = useForms();
  
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const deleteProduct = useDeleteProduct();

  const categoryOptions = useMemo(
    () => [
      'Atendimento ao Cliente',
      'Suporte Técnico',
      'Vendas e CRM',
      'Marketing',
      'Financeiro',
      'Operações',
      'Integrações Customizadas',
    ],
    []
  );

  const marketplaceStatusOptions = useMemo(
    () => [
      { value: 'draft', label: 'Rascunho' },
      { value: 'published', label: 'Publicado' },
      { value: 'archived', label: 'Arquivado' },
    ],
    []
  );

  const resetMediaState = () => {
    setThumbnailFileList([]);
    setThumbnailPreview(null);
  };

  const handleThumbnailBeforeUpload = (file: RcFile) => {
    const previewUrl = URL.createObjectURL(file);
    setThumbnailFileList([
      {
        uid: file.uid,
        name: file.name,
        status: 'done',
        originFileObj: file,
        url: previewUrl,
      },
    ]);
    setThumbnailPreview(previewUrl);
    return false;
  };

  const handleThumbnailRemove = () => {
    resetMediaState();
    form.setFieldValue('thumbnail_url', null);
  };

  const uploadThumbnailIfNeeded = async () => {
    if (thumbnailFileList.length === 0) {
      return form.getFieldValue('thumbnail_url');
    }

    const file = thumbnailFileList[0];
    const origin = file.originFileObj as RcFile | undefined;
    if (!origin) {
      return file.url;
    }

    const fileExt = origin.name.split('.').pop();
    const fileName = `${crypto.randomUUID()}.${fileExt}`;
    const filePath = `thumbnails/${fileName}`;

    const { error } = await supabase.storage
      .from('product-media')
      .upload(filePath, origin, {
        upsert: true,
        contentType: origin.type,
        cacheControl: '3600',
      });

    if (error) {
      throw error;
    }

      const { data } = supabase.storage.from('product-media').getPublicUrl(filePath);
      const publicUrl = data?.publicUrl;
  
      return publicUrl;
    };
  
    const columns: ColumnsType<Product> = [
      {
        title: 'Nome',
        dataIndex: 'name',
        key: 'name',
        render: (text: string, record: Product) => (
          <div>
            <strong>{text}</strong>
            {record.category && (
              <div>
                <Tag color="blue" style={{ fontSize: '11px' }}>
                  {record.category}
                </Tag>
              </div>
            )}
          </div>
        ),
      },
      {
        title: 'Preço/mês',
        dataIndex: 'monthly_price',
        key: 'monthly_price',
        width: 120,
        render: (price: number) => (
          price ? `R$ ${price.toFixed(2).replace('.', ',')}` : '-'
        ),
      },

      {
        title: 'Status Marketplace',
        dataIndex: 'marketplace_status',
        key: 'marketplace_status',
        width: 180,
        render: (status: string) => {
          const statusConfig = {
            draft: { color: 'default', text: 'Rascunho' },
            published: { color: 'green', text: 'Publicado' },
            archived: { color: 'red', text: 'Arquivado' },
          };
          const config = statusConfig[status as keyof typeof statusConfig] || { color: 'default', text: status };
          return <Tag color={config.color}>{config.text}</Tag>;
        },
      },
      {
        title: 'Ações',
        key: 'actions',
        width: 200,
        render: (_, record: Product) => (
          <Space>
            <Button
              type="link"
              icon={<AppstoreOutlined />}
              onClick={() => handleConfigure(record)}
            >
              Configurar
            </Button>

            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            >
              Editar
            </Button>
            <Popconfirm
              title="Tem certeza que deseja deletar este produto? Se houver contratações, ele será automaticamente arquivado."
              onConfirm={() => handleDelete(record.id)}
              okText="Sim"
              cancelText="Não"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
              >
                Deletar
              </Button>
            </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setSelectedInstanceId(product.n8n_instance_id);
    // Normalize older/alternate product_type values (e.g. 'template') to 'marketplace'
    const ptypeStr = String(product.product_type || 'marketplace');
    const normalizedType = (ptypeStr === 'template') ? 'marketplace' : (ptypeStr === 'custom' ? 'custom' : 'marketplace');
    setSelectedProductType(normalizedType as 'marketplace' | 'custom');
    
    // For custom products, fetch the client_id from client_products table
    if (product.product_type === 'custom') {
      // Find the first client that has this product assigned
      supabase
        .from('client_products')
        .select('client_id')
        .eq('product_id', product.id)
        .limit(1)
        .single()
        .then(({ data }) => {
          if (data?.client_id) {
            // Removed setSelectedClientId (unused)
            form.setFieldValue('client_id', data.client_id);
          }
        });
    }
    
    // Ensure the product_type in the form matches the normalized type and map 'name' to display field
    form.setFieldsValue({
      ...product,
      product_type: normalizedType,
      name: product.name,
    });
    if (product.thumbnail_url) {
      setThumbnailFileList([
        {
          uid: product.id,
          name: 'thumbnail',
          status: 'done',
          url: product.thumbnail_url,
        },
      ]);
      setThumbnailPreview(product.thumbnail_url);
    } else {
      resetMediaState();
    }
    
    setIsModalOpen(true);
  };

  const handleConfigure = async (product: Product) => {
    setConfiguringProduct(product);
    setIsConfigModalOpen(true);
    setLoadingConfig(true);
    
    try {
      // Use getTemplateConfig para buscar do workflow TEMPLATE
      const response = await functions.getTemplateConfig(product.id);
      
      setProductConfig(response);
      
      if (response.current_values) {
        configForm.setFieldsValue(response.current_values);
      }
    } catch (error) {
      console.error('Erro ao buscar configurações:', error);
      message.error('Erro ao carregar configurações do template');
      setProductConfig({ fields: [] });
    } finally {
      setLoadingConfig(false);
    }
  };

  const handleSaveProductConfig = async () => {
    if (!configuringProduct) return;
    
    setIsSavingConfig(true);
    try {
      const values = await configForm.validateFields();
      
      message.loading({ content: 'Salvando configuração...', key: 'saveConfig' });
      
      // Use updateTemplateConfig para salvar no workflow TEMPLATE
      await functions.updateTemplateConfig(configuringProduct.id, values);

      // Persist any configured "Field name N8N" values into the product_forms table so the sync functions
      // can use them later. We collect any form field n8n name settings from the config modal values.
      try {
        const n8nNameMap: Record<string, string> = {};
        for (const f of productConfig?.fields || []) {
          const n8nValue = values[`n8nName_${f.id}`];
          if (n8nValue) n8nNameMap[f.id] = n8nValue;
        }

        if (Object.keys(n8nNameMap).length) {
          // Fetch existing product_form for this product
          const { data: existingForm } = await supabase
            .from('product_forms')
            .select('form_fields')
            .eq('product_id', configuringProduct.id)
            .maybeSingle();

          let newFormFields: any = null;
          if (existingForm?.form_fields && Array.isArray(existingForm.form_fields)) {
            newFormFields = existingForm.form_fields.map((ff: any) => {
              const mapped = { ...ff };
              if (n8nNameMap[ff.id]) mapped.n8n_name = n8nNameMap[ff.id];
              return mapped;
            });
          } else if (productConfig?.fields && Array.isArray(productConfig.fields)) {
            // No existing product_form; create a simple form_fields array from productConfig.fields
            newFormFields = productConfig.fields.map((ff: any) => ({
              id: ff.id,
              type: ff.type,
              label: ff.label,
              n8n_name: n8nNameMap[ff.id] ?? undefined,
            }));
          }

          if (newFormFields) {
            // Upsert into product_forms table
            const { data: existingPf } = await supabase
              .from('product_forms')
              .select('id')
              .eq('product_id', configuringProduct.id)
              .maybeSingle();

            if (existingPf) {
              await supabase
                .from('product_forms')
                .update({ form_fields: newFormFields })
                .eq('product_id', configuringProduct.id);
            } else {
              await supabase
                .from('product_forms')
                .insert([{ product_id: configuringProduct.id, form_fields: newFormFields }]);
            }
            // Additionally persist a mapping in field_name_n8n for quick lookups
            try {
              const mapping = Object.keys(n8nNameMap).reduce((acc, key) => {
                acc[key] = n8nNameMap[key];
                return acc;
              }, {} as Record<string, string>);

              if (existingPf) {
                await supabase
                  .from('product_forms')
                  .update({ field_name_n8n: mapping })
                  .eq('product_id', configuringProduct.id);
              } else {
                await supabase
                  .from('product_forms')
                  .update({ field_name_n8n: mapping })
                  .eq('product_id', configuringProduct.id);
              }
            } catch (err) {
              console.warn('Failed to save field_name_n8n mapping', err);
            }

            // Refresh product listing so updated form metadata is visible
            try { queryClient.invalidateQueries({ queryKey: ['products'] }); } catch (e) { /* ignore */ }
          }
        }
      } catch (err) {
        console.error('Failed updating product_forms with n8n_name:', err);
        // Non-fatal: continue
      }
      
      message.success({ 
        content: 'Configuração salva com sucesso no workflow template!', 
        key: 'saveConfig',
        duration: 3,
      });
      
      setIsConfigModalOpen(false);
      setConfiguringProduct(null);
      configForm.resetFields();
    } catch (error: any) {
      console.error('Error saving product config:', error);
      
      if (error.errorFields) {
        message.error({ 
          content: 'Por favor, preencha todos os campos obrigatórios', 
          key: 'saveConfig' 
        });
      } else {
        const errorMessage = error.message || 'Erro ao salvar configuração';
        message.error({ 
          content: errorMessage, 
          key: 'saveConfig',
          duration: 5,
        });
      }
    } finally {
      setIsSavingConfig(false);
    }
  };

  const renderConfigField = (field: any) => {
    const commonProps = {
      key: field.id,
      label: field.label,
      name: field.id,
      rules: field.required ? [{ required: true, message: `${field.label} é obrigatório` }] : [],
      initialValue: field.current_value,
    };

    switch (field.type) {
      case 'number':
        return (
          <Form.Item {...commonProps}>
            <InputNumber style={{ width: '100%' }} placeholder={field.label} />
          </Form.Item>
        );
      
      case 'boolean':
        return (
          <Form.Item {...commonProps} valuePropName="checked">
            <Checkbox>{field.label}</Checkbox>
          </Form.Item>
        );
      
      case 'textarea':
        return (
          <Form.Item {...commonProps}>
            <Input.TextArea 
              rows={4} 
              placeholder={field.label}
              style={{ fontFamily: field.current_value?.includes('={{') ? 'monospace' : 'inherit' }}
            />
          </Form.Item>
        );
      
      case 'text':
      default:
        return (
          <Form.Item {...commonProps}>
            <Input placeholder={field.label} />
          </Form.Item>
        );
    }
  };

  // Normalize node name for matching 'Geral - Parametros' (accent-insensitive)
  const normalizeNodeName = (name?: string) => {
    if (!name) return '';
    return name
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  };

  const isGeralParametrosNode = (rawName?: string | null) => {
    const normalized = normalizeNodeName(rawName || '');
    return normalized.includes('geral') && normalized.includes('param');
  };

  const handleDelete = async (id: string) => {
    try {
      // Check whether there are any client_products associated with this product
      const { error: countErr, count } = await supabase
        .from('client_products')
        .select('id', { count: 'exact', head: true })
        .eq('product_id', id);

      if (countErr) {
        console.error('Erro ao verificar client_products para produto:', countErr);
        message.error('Erro ao verificar contratações do produto');
        return;
      }

      if (count && count > 0) {
        // Product has contracted clients: archive instead of deleting
        try {
          await updateProduct.mutateAsync({ id, marketplace_status: 'archived' });
          message.success('Produto arquivado com sucesso (não pode ser deletado porque possui contratações)');
        } catch (e) {
          console.error('Erro ao arquivar produto:', e);
          message.error('Erro ao arquivar produto');
        }
        return;
      }

      // No contracted products - safe to delete
      await deleteProduct.mutateAsync(id);
      message.success('Produto deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar produto:', error);
      message.error('Erro ao deletar produto');
    }
  };

  // ...existing code...

  const handleSubmit = async (values: any) => {
    setIsSubmitting(true);
    try {
      const thumbnailUrl = await uploadThumbnailIfNeeded();
      
      // Produto gratuito: forçar setup_fee e trial_days como null
      const isFreeProduct = !values.monthly_price || values.monthly_price <= 0;
      const effectiveSetupFee = isFreeProduct ? null : values.setup_fee;
      const effectiveTrialDays = isFreeProduct ? null : values.trial_days;

      // Para produtos custom, criar produto básico e depois associar ao cliente
      if (values.product_type === 'custom' && !editingProduct) {
        // 1. Criar produto sem workflow_id (será clonado do templateCustomizado)
        const payload = {
          product_type: 'custom',
          display_name: values.name || null,
          dashboard_webhook_url: values.dashboard_webhook_url || null,
          n8n_instance_id: values.n8n_instance_id,
          workflow_id: null, // Custom products don't have a template workflow_id
          name: values.name,
          description: values.description,
          short_description: values.short_description,
          full_description: values.full_description,
          category: values.category,
          thumbnail_url: thumbnailUrl,
          // video_url: values.video_url,
          monthly_price: values.monthly_price,
          setup_fee: effectiveSetupFee,
          trial_days: effectiveTrialDays,
          marketplace_status: 'draft', // Custom sempre draft
          form_id: values.form_id || null,
          schema: { version: '1.0.0', sections: [] },
          is_enabled: true,
        };

        console.log('📦 Custom Product payload:', payload);
        const resp = await functions.adminCreateProduct(payload as any);
        if (!resp || !resp.success) {
          console.error('Failed to create product via admin-create-product:', resp);
          throw new Error(resp?.error || 'Failed to create product via admin-create-product');
        }
        const createdProduct = resp.product;
        console.log('✅ Product created via admin-create-product:', createdProduct);

        // 2. Associar ao cliente selecionado (isso clonará o templateCustomizado)
        console.log('🔗 Associating custom product to client:', values.client_id);
        const { data: assignData, error: assignError } = await supabase.functions.invoke('admin-assign-product', {
          body: {
            client_id: values.client_id,
            product_id: createdProduct.id,
          },
        });

        console.log('📊 Assign result:', { assignData, assignError });

        if (assignError) {
          console.error('❌ Error assigning product:', assignError);
          // Even if assignment fails, the product was created
          message.warning('Produto criado mas houve erro ao associar ao cliente. Tente associar manualmente.');
          queryClient.invalidateQueries({ queryKey: ['products'] });
          handleCancel();
          return;
        }

        console.log('✅ Product successfully assigned to client');
        message.success('Produto customizado criado e associado ao cliente com sucesso!');
        queryClient.invalidateQueries({ queryKey: ['products'] });
        handleCancel();
        return;
      } else {
        // Para produtos marketplace ou edição, usar fluxo normal
        const payload = {
          product_type: values.product_type || 'marketplace',
          display_name: values.name || null,
          dashboard_webhook_url: values.product_type === 'custom' ? values.dashboard_webhook_url : null,
          n8n_instance_id: values.n8n_instance_id,
          workflow_id: values.workflow_id,
          name: values.name,
          description: values.description,
          short_description: values.short_description,
          full_description: values.full_description,
          category: values.category,
          thumbnail_url: thumbnailUrl,
          // video_url: values.video_url,
          monthly_price: values.monthly_price,
          setup_fee: effectiveSetupFee,
          trial_days: effectiveTrialDays,
          marketplace_status: values.product_type === 'marketplace' ? values.marketplace_status : 'draft',
          form_id: values.form_id || null,
          schema: editingProduct?.schema ?? { version: '1.0.0', sections: [] },
          is_enabled: values.is_enabled ?? true,
        };

        console.log('📦 Product payload:', payload);

        if (editingProduct) {
          await updateProduct.mutateAsync({
            id: editingProduct.id,
            ...payload,
          });
          message.success('Produto atualizado com sucesso');
        } else {
          await createProduct.mutateAsync(payload);
          message.success('Produto criado com sucesso');
        }
      }

      setIsModalOpen(false);
      form.resetFields();
      setEditingProduct(null);
  // Removed setSelectedClientId (unused)
      resetMediaState();
      queryClient.invalidateQueries({ queryKey: ['admin-client-products'] });
    } catch (error: any) {
      console.error('Erro ao salvar produto:', error);
      const errorMessage = error?.message || 'Erro ao salvar produto';
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedInstanceId(undefined);
    setSelectedProductType('marketplace');
  // Removed setSelectedClientId (unused)
    form.resetFields();
    setEditingProduct(null);
    resetMediaState();
  };

  // Debug effect para monitorar mudanças nos workflows
  React.useEffect(() => {
    console.log('=== WORKFLOWS DEBUG ===');
    console.log('selectedInstanceId:', selectedInstanceId);
    console.log('isLoadingWorkflows:', isLoadingWorkflows);
    console.log('workflows array:', workflows);
    console.log('workflows count:', workflows?.length || 0);
    if (workflows && workflows.length > 0) {
      console.log('first workflow:', workflows[0]);
    }
  }, [selectedInstanceId, workflows, isLoadingWorkflows]);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Produtos</Title>
        <Space>
          <Select
            style={{ width: 200 }}
            value={productTypeFilter}
            onChange={setProductTypeFilter}
            placeholder="Filtrar por tipo"
          >
            <Select.Option value="all">Todos os Produtos</Select.Option>
            <Select.Option value="marketplace">Status Marketplace</Select.Option>
            <Select.Option value="custom">Customizados</Select.Option>
          </Select>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            Novo Produto
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={filteredProducts}
        rowKey="id"
        loading={isLoading || deleteProduct.isPending}
      />

      <Modal
        title={editingProduct ? 'Editar Produto' : 'Novo Produto'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={800}
        closable={!isSubmitting}
        maskClosable={!isSubmitting}
        keyboard={!isSubmitting}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={isSubmitting}
        >
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                label="Tipo de Produto"
                name="product_type"
                rules={[{ required: true, message: 'Por favor, selecione o tipo de produto' }]}
                initialValue="marketplace"
              >
                <Select
                  placeholder="Selecione o tipo de produto"
                  disabled={isSubmitting || !!editingProduct}
                  onChange={(value) => {
                    setSelectedProductType(value as 'marketplace' | 'custom');
                    // Reset workflow and client selection when changing type
                    setSelectedInstanceId(undefined);
                    // Removed setSelectedClientId (unused)
                    form.setFieldValue('n8n_instance_id', undefined);
                    form.setFieldValue('workflow_id', undefined);
                    form.setFieldValue('client_id', undefined);
                  }}
                >
                  <Select.Option value="marketplace">Marketplace (Template)</Select.Option>
                  <Select.Option value="custom">Custom (Workflow Específico)</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                label="Instância N8N"
                name="n8n_instance_id"
                rules={[{ required: true, message: 'Por favor, selecione uma instância' }]}
              >
                <Select
                  placeholder="Selecione uma instância"
                  disabled={isSubmitting || !!editingProduct}
                  onChange={(value) => {
                    setSelectedInstanceId(value);
                    form.setFieldValue('workflow_id', undefined);
                  }}
                >
                  {instances.map((instance) => (
                    <Select.Option key={instance.id} value={instance.id}>
                      {instance.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              {/* Campo de seleção de cliente - apenas para produtos custom */}
              {selectedProductType === 'custom' && (
                <Form.Item
                  label="Cliente"
                  name="client_id"
                  rules={[{ required: true, message: 'Por favor, selecione o cliente' }]}
                  extra="O workflow será clonado automaticamente do template 'templateCustomizado' e associado a este cliente"
                >
                  <Select
                    placeholder="Selecione o cliente"
                    disabled={!!editingProduct}
                    loading={loadingClients}
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.children).toLowerCase().includes(input.toLowerCase())
                    }
                    // Removed setSelectedClientId (unused)
                  >
                    {clients.map((client: any) => (
                      <Select.Option key={client.id} value={client.id}>
                        <Space>
                          <UserOutlined />
                          {client.full_name || client.email}
                        </Space>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              {/* Campo de workflow - apenas para produtos marketplace */}
              {selectedProductType === 'marketplace' && (
                <Form.Item
                  label="Workflow no n8n (tag: Template)"
                  name="workflow_id"
                  rules={[{ required: true, message: 'Por favor, selecione um workflow' }]}
              >
                <Select
                  placeholder={selectedInstanceId ? 'Selecione um workflow' : 'Primeiro selecione uma instância'}
                  disabled={!selectedInstanceId || !!editingProduct || isSubmitting}
                  loading={isLoadingWorkflows}
                  showSearch
                  optionLabelProp="label"
                  filterOption={(input, option) => {
                    const workflowName = option?.label || '';
                    return String(workflowName).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {workflows && workflows.length > 0 ? (
                    workflows.map((workflow: any) => (
                      <Select.Option
                        key={workflow.id}
                        value={workflow.id}
                        label={workflow.name ? `${workflow.name} (${workflow.id})` : workflow.id}
                      >
                        {workflow.name ? `${workflow.name} (${workflow.id})` : workflow.id}
                      </Select.Option>
                    ))
                  ) : (
                    <Select.Option disabled>
                      {isLoadingWorkflows ? 'Carregando workflows...' : 'Nenhum workflow disponível para esta instância/pasta.'}
                    </Select.Option>
                  )}
                </Select>
                {(!isLoadingWorkflows && workflows.length === 0 && selectedInstanceId && !editingProduct) && (
                  <Alert
                    message="Nenhum workflow encontrado"
                    description="Verifique se há workflows na pasta de templates selecionada ou revise o hook useN8nWorkflows."
                    type="warning"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
                </Form.Item>
              )}

              {/* Mensagem explicativa para produtos custom */}
              {selectedProductType === 'custom' && (
                <Alert
                  message="Workflow Automático"
                  description="Para produtos customizados, o sistema buscará automaticamente o workflow com a tag 'templateCustomizado' na instância selecionada e criará uma cópia personalizada para o cliente."
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Formulário associado ao produto */}
              <Form.Item
                label="Formulário de Cadastro"
                name="form_id"
                tooltip="Selecione qual formulário será usado pelos clientes deste produto"
              >
                <Select
                  placeholder={isLoadingForms ? 'Carregando formulários...' : 'Selecione um formulário (opcional)'}
                  loading={isLoadingForms}
                  disabled={isSubmitting || !!editingProduct}
                  allowClear
                  showSearch
                  optionLabelProp="label"
                  filterOption={(input, option) => {
                    const formName = option?.label || '';
                    return String(formName).toLowerCase().includes(input.toLowerCase());
                  }}
                >
                  {forms && forms.length > 0 ? (
                    forms.map((form) => (
                      <Select.Option
                        key={form.id}
                        value={form.id}
                        label={form.name}
                      >
                        <div>
                          <div style={{ fontWeight: 500 }}>{form.name}</div>
                          {form.description && (
                            <div style={{ fontSize: '12px', color: '#888' }}>
                              {form.description}
                            </div>
                          )}
                        </div>
                      </Select.Option>
                    ))
                  ) : (
                    <Select.Option disabled>
                      {isLoadingForms ? 'Carregando...' : 'Nenhum formulário cadastrado. Crie formulários na página de Formulários.'}
                    </Select.Option>
                  )}
                </Select>
                {forms && forms.length === 0 && !isLoadingForms && (
                  <Alert
                    message="Nenhum formulário disponível"
                    description="Para associar formulários aos produtos, primeiro cadastre formulários na página de Gerenciar Formulários."
                    type="info"
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  {/* Removed display_name field (unused) - using 'name' instead as primary display value */}
                </Col>
                {selectedProductType === 'custom' && (
                  <Col span={12}>
                    <Form.Item
                      label="URL do Dashboard Webhook"
                      name="dashboard_webhook_url"
                      tooltip="URL do webhook que retorna dados do dashboard customizado"
                    >
                      <Input placeholder="https://..." />
                    </Form.Item>
                  </Col>
                )}
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                      label="Nome do produto (Exibição)"
                      name="name"
                      rules={[{ required: true, message: 'Por favor, insira o nome' }]}
                    >
                      <Input placeholder="Meu Produto" />
                    </Form.Item>
                  </Col>
                <Col span={12}>
                  <Form.Item
                    label="Categoria"
                    name="category"
                  >
                    <Select allowClear placeholder="Selecione uma categoria">
                      {categoryOptions.map((category) => (
                        <Select.Option key={category} value={category}>
                          {category}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item noStyle shouldUpdate={(prev, cur) => prev.monthly_price !== cur.monthly_price}>
                {({ getFieldValue, setFieldsValue }) => {
                  const price = getFieldValue('monthly_price');
                  const isFree = !price || price <= 0;
                  // When switching to free, clear setup_fee and trial_days
                  if (isFree && (getFieldValue('setup_fee') || getFieldValue('trial_days'))) {
                    setTimeout(() => setFieldsValue({ setup_fee: null, trial_days: null }), 0);
                  }
                  return (
                    <Row gutter={16}>
                      <Col span={isFree ? 24 : 8}>
                        <Form.Item
                          label="Preço mensal"
                          name="monthly_price"
                          extra={isFree ? 'Produto gratuito — sem forma de pagamento nem período de teste.' : undefined}
                        >
                          <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            step={10}
                            placeholder="0,00"
                            addonBefore="R$"
                          />
                        </Form.Item>
                      </Col>
                      {!isFree && (
                        <>
                          <Col span={8}>
                            <Form.Item
                              label="Taxa de setup"
                              name="setup_fee"
                            >
                              <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                step={10}
                                placeholder="0,00"
                                addonBefore="R$"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={8}>
                            <Form.Item
                              label="Trial (dias)"
                              name="trial_days"
                            >
                              <InputNumber style={{ width: '100%' }} min={0} placeholder="0" />
                            </Form.Item>
                          </Col>
                        </>
                      )}
                    </Row>
                  );
                }}
              </Form.Item>

              {selectedProductType === 'marketplace' && (
                <Form.Item
                  label="Status no marketplace"
                  name="marketplace_status"
                >
                  <Select placeholder="Selecione um status">
                    {marketplaceStatusOptions.map((status) => (
                      <Select.Option key={status.value} value={status.value}>
                        {status.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                label="Descrição curta"
                name="short_description"
              >
                <Input placeholder="Resumo de até 120 caracteres" maxLength={140} />
              </Form.Item>

              <Form.Item
                label="Descrição completa"
                name="full_description"
              >
                <TextArea rows={5} placeholder="Use HTML básico ou markdown renderizado" />
              </Form.Item>

              <Form.Item
                label="Descrição interna"
                name="description"
              >
                <TextArea rows={3} placeholder="Descrição interna ou notas adicionais" />
              </Form.Item>

               <Form.Item
                label="URL do vídeo de demonstração"
                name="video_url"
                rules={[{ type: 'url', message: 'Informe uma URL válida' }]}
              >
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Divider orientation="left">Thumb do Marketplace</Divider>
              <Form.Item name="thumbnail_url" hidden>
                <Input type="hidden" />
              </Form.Item>
              <Upload
                listType="picture-card"
                fileList={thumbnailFileList}
                onRemove={handleThumbnailRemove}
                beforeUpload={handleThumbnailBeforeUpload}
                maxCount={1}
                accept="image/png,image/jpeg,image/webp"
              >
                {thumbnailFileList.length >= 1 ? null : 'Upload'}
              </Upload>
              {thumbnailPreview && (
                <Image
                  src={thumbnailPreview}
                  alt="Pré-visualização"
                  style={{ borderRadius: 8, marginTop: 8 }}
                />
              )}
              <Tooltip title="Imagens 1200x630px recomendadas (JPG ou PNG).">
                <Typography.Text type="secondary">Ideal para cards do marketplace.</Typography.Text>
              </Tooltip>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting || createProduct.isPending || updateProduct.isPending}
              >
                {isSubmitting ? 'Salvando...' : (editingProduct ? 'Atualizar' : 'Criar')}
              </Button>
              <Button onClick={handleCancel} disabled={isSubmitting}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Configuração de Variáveis */}
      <Modal
        title={`Configurar Variáveis: ${configuringProduct?.name || ''}`}
        open={isConfigModalOpen}
        onCancel={() => {
          setIsConfigModalOpen(false);
          setConfiguringProduct(null);
          configForm.resetFields();
        }}
        footer={null}
        width={700}
        closable={!isSavingConfig}
        maskClosable={!isSavingConfig}
        keyboard={!isSavingConfig}
      >
        <Form
          form={configForm}
          layout="vertical"
          onFinish={handleSaveProductConfig}
          disabled={isSavingConfig}
        >
          {loadingConfig ? (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <Spin size="large" />
              <Paragraph style={{ marginTop: 16 }}>Carregando configurações...</Paragraph>
            </div>
          ) : !productConfig || !productConfig.fields || productConfig.fields.length === 0 || !isGeralParametrosNode(productConfig?._debug?.matched_node) ? (
            <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
              <SettingOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <Paragraph>Este produto não possui variáveis configuráveis.</Paragraph>
              <Paragraph type="secondary">
                Adicione um node "Geral - Parâmetros" (tipo Set) no workflow para configurá-lo.
              </Paragraph>
            </div>
          ) : (
            <>
              <Alert
                message="Configuração do Produto"
                description={`Workflow: ${productConfig.workflow_name || 'N/A'} • ${productConfig.fields.length} variável(is)`}
                type="info"
                showIcon
                style={{ marginBottom: 24 }}
              />
              {productConfig.fields.map((field: any) => (
                <div key={field.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: '1px dashed #eee' }}>
                  {renderConfigField(field)}
                  <Form.Item
                    label="Field name N8N"
                    name={`n8nName_${field.id}`}
                    initialValue={field.n8n_name ?? field.id}
                    tooltip="Nome da variável usada no workflow n8n. Se vazio será usado o id do campo."
                  >
                    <Input placeholder={`Nome da variável no n8n para ${field.label}`} />
                  </Form.Item>
                </div>
              ))}
              <Form.Item>
                <Space>
                  <Button 
                    type="primary" 
                    htmlType="submit"
                    loading={isSavingConfig}
                  >
                    {isSavingConfig ? 'Salvando...' : 'Salvar Configuração'}
                  </Button>
                  <Button onClick={() => {
                    setIsConfigModalOpen(false);
                    setConfiguringProduct(null);
                    configForm.resetFields();
                  }} disabled={isSavingConfig}>
                    Cancelar
                  </Button>
                </Space>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};
